import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../components/layout/MainLayout';
import statusPageApi, { StatusPage } from '../../services/api/status-page.api';
import { usePermissions } from '../../hooks/usePermissions';

export default function StatusPagesPage() {
  const { t } = useTranslation(['statusPages', 'common']);
  const navigate = useNavigate();
  const { canCreateStatusPages, canEditStatusPages, canDeleteStatusPages } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [statusPages, setStatusPages] = useState<StatusPage[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    public_url_slug: '',
    description: '',
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStatusPages();
  }, []);

  const loadStatusPages = async () => {
    try {
      setLoading(true);
      console.log('[StatusPages] Loading status pages...');
      const response = await statusPageApi.getAll();
      console.log('[StatusPages] Response:', response);
      console.log('[StatusPages] Response.success:', response.success);
      console.log('[StatusPages] Response.data:', response.data);
      console.log('[StatusPages] Response.data length:', response.data?.length);

      if (response.success) {
        console.log('[StatusPages] Setting status pages:', response.data);
        setStatusPages(response.data);
      } else {
        console.log('[StatusPages] Response not successful');
      }
    } catch (error) {
      console.error('[StatusPages] Error loading status pages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (creating) return; // Prevent double submission

    setCreating(true);
    setError(null);

    try {
      console.log('Creating status page with data:', formData);
      const response = await statusPageApi.create(formData);
      console.log('Create response:', response);

      if (response.success) {
        console.log('Status page created successfully');
        setShowCreateModal(false);
        setFormData({ name: '', public_url_slug: '', description: '' });
        await loadStatusPages();
      } else {
        setError(response.message || 'Failed to create status page');
      }
    } catch (error: any) {
      console.error('Error creating status page:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create status page';
      setError(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (id: string, enabled: boolean) => {
    try {
      await statusPageApi.toggle(id, !enabled);
      loadStatusPages();
    } catch (error) {
      console.error('Error toggling status page:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('statusPages:confirm.delete'))) return;

    try {
      await statusPageApi.delete(id);
      loadStatusPages();
    } catch (error) {
      console.error('Error deleting status page:', error);
    }
  };

  const getStatusBadge = (page: StatusPage) => {
    if (!page.is_enabled) {
      return <span className="inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400">{t('statusPages:disabled')}</span>;
    }
    return <span className="inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">{t('statusPages:active')}</span>;
  };

  console.log('[StatusPages] Rendering, loading:', loading, 'statusPages:', statusPages, 'length:', statusPages.length);

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('statusPages:title')}</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          {t('statusPages:subtitle')}
        </p>
      </div>

      {/* Create Button */}
      <div className="mb-6">
        <button
          onClick={() => {
            setShowCreateModal(true);
            setError(null);
          }}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
        >
          {t('statusPages:createButton')}
        </button>
      </div>

      {/* Status Pages Grid */}
      {statusPages.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center dark:border-gray-800 dark:bg-gray-900">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('statusPages:noPages')}</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {t('statusPages:noPagesDesc')}
          </p>
          <button
            onClick={() => {
              setShowCreateModal(true);
              setError(null);
            }}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            {t('statusPages:createButton')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {statusPages.map((page) => (
            <div
              key={page.id}
              className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900"
            >
              <div className="mb-4 flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {page.name}
                  </h3>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {page.description || t('statusPages:manage.noDescription')}
                  </p>
                </div>
                {getStatusBadge(page)}
              </div>

              <div className="mb-4 space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 dark:text-gray-400">{t('statusPages:publicUrl')}:</span>
                  <a
                    href={`/status/${page.public_url_slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
                  >
                    /status/{page.public_url_slug}
                  </a>
                </div>
                {page.custom_domain && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 dark:text-gray-400">{t('statusPages:customDomain')}:</span>
                    <span className="font-mono text-gray-900 dark:text-white">
                      {page.custom_domain}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 dark:text-gray-400">{t('statusPages:created')}:</span>
                  <span className="text-gray-900 dark:text-white">
                    {new Date(page.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => navigate(`/status-pages/${page.id}`)}
                  className="flex-1 rounded border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
                >
                  {t('statusPages:manageButton')}
                </button>
                <button
                  onClick={() => handleToggle(page.id, page.is_enabled)}
                  className={`px-3 py-2 rounded text-sm font-medium ${
                    page.is_enabled
                      ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400'
                      : 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/20 dark:text-green-400'
                  }`}
                >
                  {page.is_enabled ? t('statusPages:disable') : t('statusPages:enable')}
                </button>
                <button
                  onClick={() => handleDelete(page.id)}
                  className="px-3 py-2 rounded text-sm font-medium bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900/20 dark:text-red-400"
                >
                  {t('statusPages:delete')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 dark:bg-gray-900">
            <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
              {t('statusPages:createModal.title')}
            </h2>
            <form onSubmit={handleCreate}>
              {error && (
                <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-400">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('statusPages:createModal.nameRequired')}
                  </label>
                  <input
                    type="text"
                    required
                    disabled={creating}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder={t('statusPages:createModal.placeholder.name')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('statusPages:createModal.publicUrlSlugRequired')}
                  </label>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500 dark:text-gray-400">/status/</span>
                    <input
                      type="text"
                      required
                      disabled={creating}
                      value={formData.public_url_slug}
                      onChange={(e) => setFormData({ ...formData, public_url_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                      className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder={t('statusPages:createModal.placeholder.slug')}
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('statusPages:createModal.slugHelp')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('statusPages:createModal.description')}
                  </label>
                  <textarea
                    disabled={creating}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder={t('statusPages:createModal.placeholder.description')}
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  disabled={creating}
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormData({ name: '', public_url_slug: '', description: '' });
                    setError(null);
                  }}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('statusPages:createModal.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {creating && (
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  <span>{creating ? t('statusPages:createModal.creating') : t('statusPages:createModal.create')}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
